import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, LockKeyhole, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import MonerisCheckoutModal from '@/components/document-services/MonerisCheckoutModal';
import OrderSummary from '@/components/document-services/OrderSummary';
import { OrderDetails, OrderOptions } from '@/components/document-services/OrderOptions';
import ServiceFormRenderer from '@/components/document-services/ServiceFormRenderer';
import { ServiceDisabledNotice, ServiceError, ServiceLoading, ServicePageHeader } from '@/components/document-services/DocumentServicesCommon';
import {
  createMyServiceOrder,
  cancelMyServicePayment,
  getMyServiceProduct,
  startMyServiceCheckout,
  validateMyServiceProductForm,
  verifyMyServicePayment,
} from '@/lib/api';
import {
  apiErrorMessage,
  buildBreakdown,
  defaultSelection,
  optionSections,
  orderingEnabled,
  orderId,
  prefillServiceAnswers,
  productFormFields,
  productVariants,
  serviceAnswersWithDefaults,
  unwrapEntity,
  validateServiceAnswers,
  variantId,
  variantOptions,
} from '@/lib/documentServices';

export default function ServiceCheckout() {
  const { productId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const submittingRef = useRef(false);
  const [product, setProduct] = useState(null);
  const [selectedId, setSelectedId] = useState(searchParams.get('variant') || '');
  const [quantity, setQuantity] = useState(() => Math.max(1, Number(searchParams.get('quantity')) || 1));
  const [selection, setSelection] = useState({});
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [createdOrder, setCreatedOrder] = useState(null);
  const [paymentOpen, setPaymentOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await getMyServiceProduct(productId);
      const next = unwrapEntity(data, ['product']);
      const variants = productVariants(next);
      const fields = productFormFields(next);
      const safePrefill = data?.prefill || data?.form_prefill || next?.prefill || {};
      setProduct(next);
      const incoming = variants.find((variant) => variantId(variant) === searchParams.get('variant'));
      setSelectedId((current) => variants.some((variant) => variantId(variant) === current) ? current : variantId(variants[0]));
      setSelection(incoming ? variantOptions(incoming) : defaultSelection(next));
      setAnswers(prefillServiceAnswers(fields, safePrefill));
      setEnabled(orderingEnabled(data, orderingEnabled(next, true)));
    } catch (err) {
      setError(apiErrorMessage(err, 'Checkout could not be prepared.'));
    } finally {
      setLoading(false);
    }
  }, [productId, searchParams]);
  useEffect(() => { load(); }, [load]);

  const variants = useMemo(() => productVariants(product), [product]);
  const sections = useMemo(() => optionSections(product), [product]);
  const breakdown = useMemo(
    () => (product ? buildBreakdown(product, selection, quantity) : null),
    [product, selection, quantity],
  );
  // When the product exposes priced sections the pickers are the source of
  // truth; the stored id is only the fallback for a catalog without them.
  const variant = (sections.length && breakdown?.variant)
    || variants.find((item) => variantId(item) === selectedId)
    || variants[0];
  const fields = useMemo(() => productFormFields(product), [product]);
  const isAddOnOnly = ['addon', 'add_on', 'linked_order'].includes(String(product?.purchase_mode || '').toLowerCase()) || product?.standalone === false;

  const createOrder = async (event) => {
    event.preventDefault();
    if (submittingRef.current || !variant || !enabled || isAddOnOnly) return;
    const submittedAnswers = serviceAnswersWithDefaults(fields, answers, variant);
    if (submittedAnswers !== answers) setAnswers(submittedAnswers);
    const localErrors = validateServiceAnswers(fields, submittedAnswers, variant);
    setErrors(localErrors);
    if (Object.keys(localErrors).length) {
      document.getElementById(Object.keys(localErrors)[0])?.focus();
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    setError('');
    try {
      const validationPayload = {
        variant_id: variantId(variant),
        quantity: product?.quantity_allowed ? quantity : 1,
        form_answers: submittedAnswers,
      };
      const validation = await validateMyServiceProductForm(product.id || product.uuid || productId, validationPayload);
      const fieldErrors = validation?.data?.field_errors || validation?.data?.errors || {};
      if (Object.keys(fieldErrors).length) {
        setErrors(fieldErrors);
        document.getElementById(Object.keys(fieldErrors)[0])?.focus();
        return;
      }
      const { data } = await createMyServiceOrder({
        product_id: product.id || product.uuid,
        variant_id: variantId(variant),
        quantity: product?.quantity_allowed ? quantity : 1,
        form_answers: submittedAnswers,
      });
      const nextOrder = unwrapEntity(data, ['order']);
      if (!orderId(nextOrder)) throw new Error('The order was created without an identifier.');
      setCreatedOrder(nextOrder);
      setPaymentOpen(false);
    } catch (err) {
      const responseErrors = err?.response?.data?.field_errors || err?.response?.data?.errors;
      if (responseErrors && typeof responseErrors === 'object') setErrors(responseErrors);
      setError(apiErrorMessage(err, err?.message || 'The order could not be created.'));
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  if (loading) return <ServiceLoading label="Preparing checkout…" />;
  if (!product || (error && !product)) return <ServiceError message={error || 'Service not found.'} onRetry={load} />;

  const createdId = orderId(createdOrder);
  const serverIdempotencyKey = createdOrder?.checkout_idempotency_key || createdOrder?.payment_attempt?.idempotency_key || '';

  return (
    <div className="space-y-6 animate-fade-in">
      <ServicePageHeader backTo={`/student/services/${encodeURIComponent(productId)}`} backLabel="Service details" eyebrow="Secure checkout" title={product.name} description="Review your information and exact tax-exempt total before opening Moneris Checkout." />
      {!enabled && <ServiceDisabledNotice />}
      {isAddOnOnly && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          This service can only be added to an eligible existing order. <Link to="/student/orders" className="font-semibold underline">Open My Orders</Link> to check eligibility.
        </div>
      )}
      {error && product && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      {createdOrder && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-800">
          <div className="flex items-start gap-3"><CheckCircle2 className="mt-0.5 size-5 shrink-0" /><div><p className="font-semibold">Order saved</p><p className="mt-1 text-sm">Your unpaid order has been created. It will only be marked paid after MCC verifies the Moneris receipt.</p><div className="mt-3 flex flex-wrap gap-2"><Button type="button" size="sm" onClick={() => setPaymentOpen(true)} className="bg-[#062B1A] text-white">Open payment</Button><Button asChild type="button" size="sm" variant="outline"><Link to={`/student/orders/${encodeURIComponent(createdId)}`}>View order</Link></Button></div></div></div>
        </div>
      )}
      <form onSubmit={createOrder} noValidate>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
          <div className="space-y-5">
            {/* Once an order exists its options are settled, so the pickers
                step aside rather than inviting a change that would not apply. */}
            {sections.length > 0 && !createdOrder ? (
              <OrderOptions
                product={product}
                selection={selection}
                quantity={quantity}
                onSelect={(sectionId, value) => setSelection((current) => ({ ...current, [sectionId]: value }))}
                onQuantityChange={setQuantity}
              />
            ) : (
              <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
                <CardHeader><CardTitle className="text-lg text-[#062B1A]">Service options</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <fieldset><legend className="text-sm font-medium text-[#062B1A]">Select an option *</legend><div className="mt-2 grid gap-2 sm:grid-cols-2">{variants.map((item) => { const id = variantId(item); return <label key={id} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-3 text-sm ${id === variantId(variant) ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'border-[#062B1A]/10 bg-white'}`}><input type="radio" name="variant" value={id} checked={id === variantId(variant)} onChange={() => setSelectedId(id)} disabled={Boolean(createdOrder)} className="accent-[#062B1A]" />{item.name || item.label}</label>; })}</div></fieldset>
                  {product.quantity_allowed && <div className="max-w-[160px]"><label htmlFor="service-quantity" className="text-sm font-medium text-[#062B1A]">Quantity</label><input id="service-quantity" type="number" min="1" max={product.max_quantity || 10} value={quantity} onChange={(event) => setQuantity(Math.max(1, Math.min(Number(event.target.value) || 1, product.max_quantity || 10)))} disabled={Boolean(createdOrder)} className="mt-1.5 h-10 w-full rounded-md border border-[#062B1A]/15 bg-white px-3 text-sm" /></div>}
                </CardContent>
              </Card>
            )}
            {fields.length > 0 && (
              <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm"><CardHeader><CardTitle className="text-lg text-[#062B1A]">Order information</CardTitle></CardHeader><CardContent><ServiceFormRenderer fields={fields} answers={answers} onChange={setAnswers} errors={errors} variant={variant} disabled={Boolean(createdOrder)} /></CardContent></Card>
            )}
            <div className="flex items-start gap-3 rounded-xl border border-[#062B1A]/10 bg-white p-4 text-xs leading-relaxed text-[#062B1A]/55"><LockKeyhole className="mt-0.5 size-4 shrink-0 text-[#D4AF37]" />MCC sends only a server-calculated amount and a safe checkout ticket to Moneris. Card number, expiry, and CVD are never entered into this portal.</div>
          </div>
          <aside className="space-y-4 lg:sticky lg:top-6 lg:self-start">
            {/* Before the order exists the summary previews the live selection;
                afterwards it reads the saved order, which is authoritative. */}
            {createdOrder
              ? <OrderSummary order={createdOrder} />
              : <OrderDetails product={product} breakdown={breakdown} />}
            {!createdOrder && <Button type="submit" disabled={!enabled || isAddOnOnly || !variant || submitting} className="w-full bg-[#062B1A] text-white hover:bg-[#041B10]">{submitting ? <Loader2 className="mr-2 size-4 animate-spin" /> : <ShieldCheck className="mr-2 size-4" />}{submitting ? 'Creating secure order…' : 'Create order and continue to payment'}</Button>}
          </aside>
        </div>
      </form>
      {createdOrder && (
        <MonerisCheckoutModal
          open={paymentOpen}
          onOpenChange={setPaymentOpen}
          orderId={createdId}
          idempotencyKey={serverIdempotencyKey}
          startCheckout={startMyServiceCheckout}
          verifyPayment={verifyMyServicePayment}
          cancelPayment={cancelMyServicePayment}
          onVerified={(result) => navigate(`/student/orders/${encodeURIComponent(createdId)}/payment-result`, { replace: true, state: { paymentResult: result } })}
        />
      )}
    </div>
  );
}
